import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, BookOpen, ChevronLeft, Loader2, Search } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const PrecedentSearchPage = () => {
  const [query, setQuery] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/precedent/search`, { query, jurisdiction: jurisdiction || null, limit: 20 });
      setResults(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="precedent-search-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Precedent Search</h1>
            <Badge className="bg-teal-500">LIVE DATA</Badge>
          </div>
          <p className="text-muted-foreground">Search millions of court opinions from CourtListener</p>
        </div>
        
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[300px] space-y-1.5">
                <Label>Legal Issue or Keywords</Label>
                <Input 
                  value={query} 
                  onChange={e => setQuery(e.target.value)}
                  placeholder='e.g., "negligence per se" traffic violation'
                  onKeyDown={e => e.key === 'Enter' && search()}
                />
              </div>
              <div className="w-48 space-y-1.5">
                <Label>Jurisdiction</Label>
                <Select value={jurisdiction} onValueChange={setJurisdiction}>
                  <SelectTrigger><SelectValue placeholder="All Courts" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Federal Courts</SelectItem>
                    <SelectItem value="scotus">Supreme Court</SelectItem>
                    <SelectItem value="ca9">9th Circuit</SelectItem>
                    <SelectItem value="ca2">2nd Circuit</SelectItem>
                    <SelectItem value="ca5">5th Circuit</SelectItem>
                    <SelectItem value="ca11">11th Circuit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={search} disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BookOpen className="h-4 w-4 mr-2" />}
                  Search Cases
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {results && (
          <div className="space-y-4 animate-fade-in">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Found {results.results_count} cases • Source: {results.data_source}
              </p>
            </div>
            
            {results.results.map((result, i) => (
              <Card key={i} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-lg">{result.case_name}</h3>
                    <Badge variant="outline">{result.date_filed}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{result.court}</p>
                  <p className="text-sm mb-3 line-clamp-3">{result.snippet}</p>
                  <a 
                    href={result.absolute_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    View Full Opinion <ArrowRight className="h-3 w-3" />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default PrecedentSearchPage;
